'use client';

import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@shared/components/shadcn';
import { createDebug } from '@shared/debug';
import { isBusinessUatCookieSet, removeBetaCookie, ROUTES, setPreferLegacyCookie, toLegacyRelativePath, useClientConfig } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

/**
 * Normalizes the route for the legacy site.
 * Currently covers only lotteries routes, because in the legacy site,
 * the lotteries routes are not prefixed with /lotteries (Chroma enhancement).
 * Add more specific cases if needed for other product verticals
 * @param route - The route to normalize.
 * @returns The normalized route.
 */
function normalizeRouteForLegacy(route: string): string {
  const lottoDiscovery = ROUTES.LOTTO.DISCOVERY;
  const lottoDiscoveryPathPrefix = `${lottoDiscovery}/`;
  if (route.startsWith(lottoDiscoveryPathPrefix)) return route.slice(lottoDiscovery.length);
  return route;
}

function ChromaOrLegacyDialog() {
  const config = useClientConfig();
  const debug = createDebug({ isEnabled: false });
  const t = useTranslations('global_dialogs.chroma_or_legacy_dialog');
  const [isOpen, setIsOpen] = useState(false);

  const businessUatCookieIsSet = isBusinessUatCookieSet();
  const LEGACY_ROUTES = {
    ...(!businessUatCookieIsSet
      ? {
          [ROUTES.LOTTO.DISCOVERY]: t('messages.lotteries'),
          [ROUTES.SCRATCHCARDS.DISCOVERY]: t('messages.scratchcards'),
        }
      : {}),
    [ROUTES.FOOTER.NAVIGATION.LOTTERY.RESULTS]: t('messages.lotteries'),
    [ROUTES.FOOTER.NAVIGATION.LOTTERY.SYNDICATES]: t('messages.syndicates'),
    [ROUTES.SPORTS.HELP_GUIDE]: t('messages.sports'),
    [ROUTES.LEGACY_PURCHASE.MOBILE]: t('messages.leaving_beta_site'),
    [ROUTES.LEGACY_PURCHASE.DESKTOP]: t('messages.leaving_beta_site'),
  } as const;

  const [attemptedRoute, setAttemptedRoute] = useState<string | undefined>(undefined);

  const LEGACY_PATH_PREFIXES = Object.keys(LEGACY_ROUTES);

  const getMatchedRoute = () => {
    return LEGACY_PATH_PREFIXES.find(prefix => attemptedRoute?.startsWith(prefix));
  };

  const getMessage = () => {
    const matchedRoute = getMatchedRoute();
    return matchedRoute ? LEGACY_ROUTES[matchedRoute as keyof typeof LEGACY_ROUTES] : t('messages.generic');
  };

  useEffect(() => {
    const open = (ev: Event) => {
      try {
        const custom = ev as CustomEvent<{ href?: string }>;
        if (custom.detail?.href) {
          setAttemptedRoute(custom.detail.href);
        }
        setIsOpen(true);
      } catch (error) {
        debug.error('Error handling legacy:open event:', error);
        setIsOpen(true);
      }
    };

    window.addEventListener('legacy:open', open);
    return () => window.removeEventListener('legacy:open', open);
  }, [debug]);

  const closeDialog = () => {
    setIsOpen(false);
  };

  const continueToLegacy = (): void => {
    removeBetaCookie();
    setPreferLegacyCookie();

    try {
      if (!attemptedRoute) {
        window.location.href = config.legacySiteUrl;
        return;
      }

      const legacyPath = toLegacyRelativePath(attemptedRoute, config.legacySiteUrl, window.location.origin);
      if (legacyPath) {
        window.location.href = `${config.legacySiteUrl}${normalizeRouteForLegacy(legacyPath)}`;
      } else if (attemptedRoute.startsWith('/') && !attemptedRoute.startsWith('//')) {
        // A root-relative route must never stay on Chroma, even if it could not be parsed.
        window.location.href = `${config.legacySiteUrl}${normalizeRouteForLegacy(attemptedRoute)}`;
      } else {
        window.location.href = attemptedRoute;
      }
    } catch (error) {
      debug.error('Error navigating to legacy site:', error);
      window.location.href = config.legacySiteUrl;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeDialog}>
      <DialogContent variant="dialog" data-testid="chroma-or-legacy-dialog" data-track-container="Classic Product Confirmation" data-track-feature="Navigation">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
        </DialogHeader>
        <DialogDescription data-testid="chroma-or-legacy-dialog-description">{getMessage()}</DialogDescription>
        <DialogFooter data-track-container="Actions">
          <Button variant="secondary" onClick={closeDialog} data-track-name="Cancel" data-testid="chroma-or-legacy-dialog-cancel">
            {t('cta.cancel')}
          </Button>
          <Button variant="primary" onClick={continueToLegacy} data-track-name="Continue" data-testid="chroma-or-legacy-dialog-continue">
            {t('cta.continue')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { ChromaOrLegacyDialog, normalizeRouteForLegacy };
